"use client";

import { useInfiniteQuery } from "@tanstack/react-query";
import type { components, Page } from "@theta/api-client";
import { thetaApi, pageOf } from "@/lib/api/theta";
import { useDebounced } from "./useDebounced";

/**
 * SITE SEARCH — `GET /search`
 *
 * `lib/api/search.ts` existed to flatten `{groups: [...], topics: [...]}` into
 * one list the UI could render, because the header dropdown and the search
 * page had each written that flattening out by hand and could drift apart.
 * There is nothing to flatten now: the route answers ONE RANKED LIST,
 * discriminated by `resultType`, cursor-paged, and the rank formula is
 * server-owned (§1.4) rather than "groups first, then topics, in the order the
 * two arrays happened to arrive".
 *
 * EVENTS ARE IN IT. `types=posts,groups,events` is the default and the old
 * response had no arm for events at all, so the search page gained a result
 * kind by moving.
 *
 * `permalink` COMES WITH EVERY HIT, so neither surface builds
 * `/grupper/{slug}/opslag/{id}` from parts any more — which is what made a
 * result's URL a thing two files could disagree about.
 *
 * A term shorter than two characters is an EMPTY PAGE, not an error, so the
 * caller does not have to guard the request; it is still not sent, because a
 * request whose answer is known is a request not worth making.
 *
 * IT IS CURSOR-PAGED, AND PAGE TWO IS REACHABLE. This asked for `limit: 20`
 * through a plain `useQuery` and threw `page.hasMore` and `nextCursor` away,
 * so a common term matching sixty posts rendered twenty links with nothing
 * saying there were more and no way to get at them. Every other listing in
 * this batch gained cursor controls; this one was the listing that did not.
 * The header dropdown reads `results` and ignores the rest — a dropdown is a
 * preview and stops at the first page by simply never asking for another.
 */

type SearchHit = components["schemas"]["SearchResult"];

const MIN_TERM = 2;
/** Well under `MAX_PAGE_LIMIT`: a search page is read, not scrolled past. */
const PAGE_SIZE = 20;

export function useSiteSearch(term: string) {
  const trimmed = useDebounced(term.trim(), 300);
  const enabled = trimmed.length >= MIN_TERM;

  const query = useInfiniteQuery({
    queryKey: ["v1", "search", trimmed],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      thetaApi.http
        .GET("/search", {
          params: {
            query: { q: trimmed, limit: PAGE_SIZE, ...(pageParam ? { cursor: pageParam } : {}) },
          },
        })
        .then(pageOf<SearchHit>),
    getNextPageParam: (last: Page<SearchHit>) => (last.hasMore ? last.nextCursor : undefined),
    enabled,
    staleTime: 60 * 1000,
  });

  const loaded = query.data?.pages.flatMap((page) => page.items) ?? [];
  return {
    results: enabled ? loaded : [],
    /** True while the user has typed past the answer we are showing. */
    isSearching: enabled && (query.isPending || trimmed !== term.trim()),
    /** The server's own answer to "is there more of this". */
    hasMore: enabled && query.hasNextPage,
    isLoadingMore: query.isFetchingNextPage,
    loadMore: () => void query.fetchNextPage(),
  };
}
